import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  Alert, Button, Card, CardBody, CustomInput, FormGroup, Input, Label, Table
} from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import { Link } from 'react-router-dom';
import { makeSendMessageDetailPath } from '../../utils/routerHelper';
import { formatAdminDateTime, competitionTimeZone } from '../../utils/adminDateTime';

interface Caps {
  isCompetitionAdmin: boolean;
  isDivisionCaptain: boolean;
  captainDivisionEntityIDs: number[];
}

interface EntityOption {
  id: number;
  name: string;
  type: string;
}

interface BroadcastRow {
  id: number;
  subject: string;
  body: string;
  audienceType: string;
  channels: string;
  recipientCount: number;
  status: string;
  createdAt: string;
  sentAt?: string;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  entities: { [id: number]: EntityOption };
  competitionTimezone?: string;
}

interface SelectableUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  level: string;
}

interface LocalState {
  caps: Caps | null;
  subject: string;
  body: string;
  channels: string;
  audienceType: string;
  audienceEntityID: string;
  selectedUserIDs: number[];
  selectableUsers: SelectableUser[];
  userFilter: string;
  previewCount: number | null;
  history: BroadcastRow[];
  error: string;
  notice: string;
  busy: boolean;
  step: 'compose' | 'preview';
}

class SendMessagePage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    caps: null,
    subject: '',
    body: '',
    channels: 'both',
    audienceType: 'division',
    audienceEntityID: '',
    selectedUserIDs: [],
    selectableUsers: [],
    userFilter: '',
    previewCount: null,
    history: [],
    error: '',
    notice: '',
    busy: false,
    step: 'compose'
  };

  public componentDidMount() {
    const locState = this.props.history.location.state as {
      duplicateSubject?: string;
      duplicateBody?: string;
      duplicateChannels?: string;
    } | undefined;
    if (locState && locState.duplicateSubject) {
      this.setState({
        subject: locState.duplicateSubject,
        body: locState.duplicateBody || '',
        channels: locState.duplicateChannels || 'both',
        step: 'compose'
      });
    }
    this.bootstrap();
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private formatWhen = (iso: string) =>
    formatAdminDateTime(iso, this.props.competitionTimezone)

  private bootstrap = async () => {
    const { apiBase, competitionName } = this.props;
    try {
      const [capsRes, histRes] = await Promise.all([
        fetch(`${apiBase}/competitions/${encodeURIComponent(competitionName)}/comms/capabilities`, {
          headers: this.authHeaders()
        }),
        fetch(`${apiBase}/competitions/${encodeURIComponent(competitionName)}/broadcasts`, {
          headers: this.authHeaders()
        })
      ]);
      if (capsRes.status === 403 || histRes.status === 403) {
        this.setState({ error: 'You do not have permission to send messages.' });
        return;
      }
      if (!capsRes.ok) {
        this.setState({ error: 'Failed to load messaging permissions.' });
        return;
      }
      const caps = await capsRes.json();
      const hist = histRes.ok ? await histRes.json() : { broadcasts: [] };
      const nextAudience = caps.isCompetitionAdmin ? 'competition' : 'division';
      const captainDivisions: number[] = caps.captainDivisionEntityIDs || [];
      this.setState({
        caps,
        audienceType: nextAudience,
        audienceEntityID: captainDivisions.length === 1 ? String(captainDivisions[0]) : '',
        history: hist.broadcasts || []
      });
      if (caps.isCompetitionAdmin) {
        this.loadSelectableUsers();
      }
    } catch (_err) {
      this.setState({ error: 'Failed to load Send Message.' });
    }
  }

  private loadSelectableUsers = async () => {
    const { apiBase, competitionName } = this.props;
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/comms/broadcast-recipients`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ selectableUsers: data.users || [] });
    } catch (_err) {
      // Picker stays empty; preview will surface validation errors.
    }
  }

  private payload = (dryRun: boolean) => {
    const { subject, body, channels, audienceType, audienceEntityID, selectedUserIDs } = this.state;
    const payload: any = {
      subject,
      body,
      channels: audienceType === 'preregistered'
        ? 'email'
        : channels === 'both' ? 'in_app,email' : channels,
      audienceType,
      dryRun
    };
    if (audienceType !== 'competition' && audienceType !== 'admins' && audienceType !== 'users' && audienceEntityID) {
      payload.audienceEntityID = Number(audienceEntityID);
    }
    if (audienceType === 'users') {
      payload.recipientUserIDs = selectedUserIDs;
    }
    return payload;
  }

  private audienceDescription = (): string => {
    const { t } = this.props;
    const { audienceType, audienceEntityID, previewCount, selectedUserIDs } = this.state;
    const entity = audienceEntityID ? this.props.entities[Number(audienceEntityID)] : null;
    if (audienceType === 'competition') {
      return previewCount != null
        ? `${t('Entire competition')} — ${previewCount} ${t('participants')}`
        : t('Entire competition');
    }
    if (audienceType === 'admins') {
      return previewCount != null
        ? `${t('All admins')} — ${previewCount} ${t('recipients')}`
        : t('All admins');
    }
    if (audienceType === 'users') {
      const n = previewCount != null ? previewCount : selectedUserIDs.length;
      return `${t('Specific users')} — ${n} ${t('selected')}`;
    }
    if (audienceType === 'preregistered') {
      return previewCount != null
        ? `${t('Pre-registered, not joined')} — ${previewCount} ${t('recipients')}`
        : t('Pre-registered, not joined');
    }
    if (entity) {
      return previewCount != null
        ? `${entity.name} — ${previewCount} ${t('participants')}`
        : entity.name;
    }
    return audienceType;
  }

  private preview = async () => {
    const { apiBase, competitionName, t } = this.props;
    const { audienceType, audienceEntityID, selectedUserIDs } = this.state;
    if ((audienceType === 'division' || audienceType === 'department') && !audienceEntityID) {
      this.setState({ error: t('Select a Division or Department first.') });
      return;
    }
    if (audienceType === 'users' && selectedUserIDs.length === 0) {
      this.setState({ error: t('Select at least one user.') });
      return;
    }
    this.setState({ busy: true, error: '', notice: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/broadcasts`,
        { method: 'POST', headers: this.authHeaders(), body: JSON.stringify(this.payload(true)) }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        this.setState({ busy: false, error: (data && data.message) || 'Preview failed.' });
        return;
      }
      this.setState({
        busy: false,
        previewCount: data.recipientCount || 0,
        step: 'preview'
      });
    } catch (_err) {
      this.setState({ busy: false, error: 'Preview failed.' });
    }
  }

  private toggleUser = (userID: number) => {
    const { selectedUserIDs } = this.state;
    const next = selectedUserIDs.indexOf(userID) >= 0
      ? selectedUserIDs.filter((id) => id !== userID)
      : selectedUserIDs.concat([userID]);
    this.setState({ selectedUserIDs: next, previewCount: null });
  }

  private filteredSelectableUsers = (): SelectableUser[] => {
    const { selectableUsers, userFilter } = this.state;
    const q = (userFilter || '').trim().toLowerCase();
    if (!q) return selectableUsers;
    return selectableUsers.filter((u) => {
      const hay = `${u.firstName} ${u.lastName} ${u.email} ${u.level}`.toLowerCase();
      return hay.indexOf(q) >= 0;
    });
  }

  private renderUserPicker() {
    const { t } = this.props;
    const { selectedUserIDs, userFilter } = this.state;
    const users = this.filteredSelectableUsers();
    return (
      <FormGroup>
        <Label>{t('Select users')}</Label>
        <Input
          type="text"
          placeholder={t('Filter by name or email…')}
          value={userFilter}
          onChange={(e) => this.setState({ userFilter: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <div
          className="cc__white_box"
          style={{ maxHeight: 260, overflowY: 'auto', padding: 8 }}
        >
          {users.length === 0 &&
            <div className="cc__lighttext">{t('No matching users.')}</div>
          }
          {users.map((u) =>
            <CustomInput
              key={u.id}
              type="checkbox"
              id={`send_user_${u.id}`}
              checked={selectedUserIDs.indexOf(u.id) >= 0}
              onChange={() => this.toggleUser(u.id)}
              label={`${u.firstName} ${u.lastName} <${u.email}>`.trim()}
            />
          )}
        </div>
        <div className="cc__lighttext" style={{ marginTop: 6 }}>
          {selectedUserIDs.length} {t('selected')}
        </div>
      </FormGroup>
    );
  }

  private send = async () => {
    const { previewCount } = this.state;
    if (previewCount == null) {
      this.setState({ error: 'Preview the message first.' });
      return;
    }
    const { apiBase, competitionName } = this.props;
    this.setState({ busy: true, error: '', notice: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/broadcasts`,
        { method: 'POST', headers: this.authHeaders(), body: JSON.stringify(this.payload(false)) }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        this.setState({ busy: false, error: (data && data.message) || 'Send failed.' });
        return;
      }
      this.setState({
        busy: false,
        notice: `Sent to ${data.recipientCount || previewCount} participants.`,
        subject: '',
        body: '',
        previewCount: null,
        step: 'compose'
      });
      this.bootstrap();
    } catch (_err) {
      this.setState({ busy: false, error: 'Send failed.' });
    }
  }

  private audienceOptions = () => {
    const { entities } = this.props;
    const { caps, audienceType } = this.state;
    if (!caps) return [];
    const list = Object.keys(entities || {}).map((id) => entities[Number(id)]).filter(Boolean);
    if (audienceType === 'division') {
      if (caps.isCompetitionAdmin) {
        return list.filter((e) => e.type === 'team');
      }
      const allowed = new Set(caps.captainDivisionEntityIDs || []);
      return list.filter((e) => e.type === 'team' && allowed.has(e.id));
    }
    if (audienceType === 'department') {
      return list.filter((e) => e.type === 'university');
    }
    return [];
  }

  private renderPreview() {
    const { t } = this.props;
    const { subject, body, channels, previewCount, busy } = this.state;
    const showEmail = channels === 'email' || channels === 'both';
    const showInApp = channels === 'in_app' || channels === 'both';
    return (
      <Card style={{ marginTop: 16 }}>
        <CardBody>
          <h3>{t('Preview message')}</h3>
          <dl>
            <dt>{t('Audience')}</dt>
            <dd>{this.audienceDescription()}</dd>
            <dt>{t('Subject')}</dt>
            <dd>{subject}</dd>
            <dt>{t('Channels')}</dt>
            <dd>{channels === 'both' ? t('In-app and email') : channels}</dd>
            <dt>{t('Recipients')}</dt>
            <dd>{previewCount}</dd>
          </dl>
          {showInApp &&
            <div style={{ marginBottom: 16 }}>
              <h5>{t('In-app version')}</h5>
              <div className="cc__white_box" style={{ padding: 12 }}>
                <strong>{subject}</strong>
                <p style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{body}</p>
              </div>
            </div>
          }
          {showEmail &&
            <div style={{ marginBottom: 16 }}>
              <h5>{t('Email version')}</h5>
              <div className="cc__white_box" style={{ padding: 12 }}>
                <div style={{ fontFamily: 'Arial, sans-serif' }}>
                  <p><strong>{subject}</strong></p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{body}</p>
                </div>
              </div>
            </div>
          }
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button color="secondary" disabled={busy} onClick={() => this.setState({ step: 'compose' })}>
              {t('Back to compose')}
            </Button>
            <Button color="primary" disabled={busy} onClick={this.send}>
              {t('Send message')}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  public render() {
    const { t } = this.props;
    const {
      caps, subject, body, channels, audienceType, audienceEntityID,
      history, error, notice, busy, step
    } = this.state;

    return (
      <div>
        <div className="container" style={{ maxWidth: 900, paddingTop: 24, paddingBottom: 40 }}>
          <h2>{t('Send Message')}</h2>
          <AdminGuideLink sectionId="communications" label={t('How announcements work')} t={t} />
          <p className="cc__lighttext">
            {t('Compose your message, preview how it will look, then send. Message history keeps a record of what was sent.')}
          </p>
          {error && <Alert color="danger">{error}</Alert>}
          {notice && <Alert color="success">{notice}</Alert>}
          {caps && step === 'compose' &&
            <div>
              <FormGroup>
                <Label>{t('Audience')}</Label>
                {caps.isCompetitionAdmin ? (
                  <Input
                    type="select"
                    value={audienceType}
                    onChange={(e) => this.setState({
                      audienceType: e.target.value,
                      audienceEntityID: '',
                      selectedUserIDs: e.target.value === 'users' ? this.state.selectedUserIDs : [],
                      channels: e.target.value === 'preregistered' ? 'email' : this.state.channels,
                      previewCount: null
                    })}
                  >
                    <option value="competition" aria-selected={audienceType === 'competition'}>{t('Entire competition')}</option>
                    <option value="admins" aria-selected={audienceType === 'admins'}>{t('All admins')}</option>
                    <option value="users" aria-selected={audienceType === 'users'}>{t('Specific users')}</option>
                    <option value="preregistered" aria-selected={audienceType === 'preregistered'}>{t('Pre-registered, not joined')}</option>
                    <option value="department" aria-selected={audienceType === 'department'}>{t('Department')}</option>
                    <option value="division" aria-selected={audienceType === 'division'}>{t('Division')}</option>
                  </Input>
                ) : (
                  <Input type="text" disabled={true} value={t('Your Division only')} />
                )}
              </FormGroup>
              {(audienceType === 'division' || audienceType === 'department') &&
                <FormGroup>
                  <Label>{audienceType === 'department' ? t('Department') : t('Division')}</Label>
                  <Input
                    type="select"
                    value={audienceEntityID}
                    onChange={(e) => this.setState({ audienceEntityID: e.target.value, previewCount: null })}
                  >
                    <option value="" aria-selected={audienceEntityID === ''}>{t('Select…')}</option>
                    {this.audienceOptions().map((e) =>
                      <option
                        key={e.id}
                        value={e.id}
                        aria-selected={audienceEntityID === String(e.id)}
                      >
                        {e.name}
                      </option>
                    )}
                  </Input>
                </FormGroup>
              }
              {audienceType === 'users' && caps.isCompetitionAdmin && this.renderUserPicker()}
              <FormGroup>
                <Label>{t('Channel')}</Label>
                {audienceType === 'preregistered' ? (
                  <Alert color="info">
                    {t('Email only. People who have not joined do not yet have an in-app inbox. Only preregistrants who opted into Challenge updates are included.')}
                  </Alert>
                ) : (
                  <div>
                    {(['in_app', 'email', 'both'] as const).map((ch) =>
                      <CustomInput
                        key={ch}
                        type="radio"
                        id={`ch_${ch}`}
                        name="channels"
                        label={ch === 'both' ? t('Both') : ch === 'in_app' ? t('In-app') : t('Email')}
                        checked={channels === ch}
                        onChange={() => this.setState({ channels: ch, previewCount: null })}
                      />
                    )}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label>{t('Subject')}</Label>
                <Input value={subject} onChange={(e) => this.setState({ subject: e.target.value, previewCount: null })} />
              </FormGroup>
              <FormGroup>
                <Label>{t('Message')}</Label>
                <Input
                  type="textarea"
                  rows={6}
                  value={body}
                  onChange={(e) => this.setState({ body: e.target.value, previewCount: null })}
                />
              </FormGroup>
              <Button color="primary" disabled={busy || !subject || !body} onClick={this.preview}>
                {t('Preview message')}
              </Button>
            </div>
          }
          {caps && step === 'preview' && this.renderPreview()}

          <h3 style={{ marginTop: 36 }}>{t('Message History')}</h3>
          <Table responsive={true} size="sm">
            <thead>
              <tr>
                <th>{t('When')}</th>
                <th>{t('Subject')}</th>
                <th>{t('Audience')}</th>
                <th>{t('Channels')}</th>
                <th>{t('Recipients')}</th>
                <th>{t('Status')}</th>
                <th>{t('View')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) =>
                <tr key={row.id}>
                  <td>{this.formatWhen(row.sentAt || row.createdAt)}</td>
                  <td>
                    <Link to={makeSendMessageDetailPath(row.id, t)}>{row.subject}</Link>
                  </td>
                  <td>{row.audienceType}</td>
                  <td>{row.channels}</td>
                  <td>{row.recipientCount}</td>
                  <td>{row.status}</td>
                  <td>
                    <Link to={makeSendMessageDetailPath(row.id, t)}>{t('Open')}</Link>
                  </td>
                </tr>
              )}
              {history.length === 0 &&
                <tr><td colSpan={7} className="cc__lighttext">{t('No messages sent yet.')}</td></tr>
              }
            </tbody>
          </Table>
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  entities: (state.entities || {}) as any,
  competitionTimezone: competitionTimeZone(state.competition)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(SendMessagePage));
